import { useMemo } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Check, Moon, Radio, Sun } from 'lucide-react';
import { Kbd } from '@/components/ui/kbd';
import {
  SHORTCUT_GROUPS,
  isMacPlatform,
  renderKeyToken,
  type Shortcut,
} from './shortcuts-catalog';
import { useTheme, type Theme } from './useTheme';

function ThemeTile({
  value,
  current,
  label,
  icon,
  onSelect,
}: {
  value: Theme;
  current: Theme;
  label: string;
  icon: React.ReactNode;
  onSelect: (next: Theme) => void;
}) {
  const selected = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={selected}
      className={
        'group relative flex flex-col items-start gap-3 p-4 rounded-lg border text-left transition-colors duration-fast min-h-[120px] ' +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
        (selected
          ? 'border-primary bg-primary/10'
          : 'border-border bg-bg-card hover:bg-bg-card-hover hover:border-border-strong')
      }
    >
      <span
        className={
          'inline-flex w-9 h-9 items-center justify-center rounded-md ' +
          (selected ? 'bg-primary text-primary-fg' : 'bg-bg-muted text-fg-muted')
        }
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-fg">{label}</span>
        <span className="text-xs text-fg-muted">
          {value === 'dark' ? 'Padrão. Recomendado para uso prolongado.' : 'Alto contraste em ambientes iluminados.'}
        </span>
      </span>
      {selected && (
        <span className="absolute top-3 right-3 inline-flex w-5 h-5 items-center justify-center rounded-full bg-primary text-primary-fg">
          <Check size={12} strokeWidth={2.5} aria-hidden="true" />
        </span>
      )}
    </button>
  );
}

function ShortcutListRow({ item, mac }: { item: Shortcut; mac: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 px-4 border-b border-border last:border-b-0">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm text-fg">{item.name}</span>
        {item.desc && <span className="text-xs text-fg-muted">{item.desc}</span>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {item.keys.map((k, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span aria-hidden="true" className="text-[10px] text-fg-subtle">+</span>}
            <Kbd>{renderKeyToken(k, mac)}</Kbd>
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-bg-elevated overflow-hidden">
      <header className="px-5 pt-4 pb-3 border-b border-border">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        {description && <p className="text-xs text-fg-muted mt-1">{description}</p>}
      </header>
      <div>{children}</div>
    </section>
  );
}

export function SettingsPage() {
  const [theme, setTheme] = useTheme();
  const mac = useMemo(() => isMacPlatform(), []);

  return (
    <div className="min-h-screen bg-bg">
      <header
        role="banner"
        className="flex items-center gap-3 h-14 px-4 bg-bg-elevated border-b border-border sticky top-0 z-10"
      >
        <Link
          to="/painel"
          className="inline-flex items-center gap-2 h-8 px-3 rounded-md text-sm text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors duration-fast focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ArrowLeft size={14} strokeWidth={1.75} aria-hidden="true" />
          Voltar ao painel
        </Link>
        <div className="h-5 w-px bg-border mx-1" aria-hidden="true" />
        <div className="flex items-center gap-2">
          <span
            className="w-[22px] h-[22px] rounded-[5px] grid place-items-center text-primary-fg"
            style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(192 76% 42%))' }}
            aria-hidden="true"
          >
            <Radio size={13} strokeWidth={2.5} />
          </span>
          <h1 className="text-base font-semibold tracking-tight">Configurações</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 flex flex-col gap-6">
        <SectionCard title="Tema" description="Aparência aplicada em todo o painel.">
          <div className="grid grid-cols-2 gap-3 p-4">
            <ThemeTile
              value="dark"
              current={theme}
              label="Escuro"
              icon={<Moon size={18} strokeWidth={1.75} />}
              onSelect={setTheme}
            />
            <ThemeTile
              value="light"
              current={theme}
              label="Claro"
              icon={<Sun size={18} strokeWidth={1.75} />}
              onSelect={setTheme}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Atalhos de teclado"
          description="Combinações com modificador funcionam mesmo durante digitação."
        >
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-subtle px-4 pt-4 pb-2">
                {group.label}
              </div>
              <div>
                {group.items.map((item) => (
                  <ShortcutListRow key={item.name} item={item} mac={mac} />
                ))}
              </div>
            </div>
          ))}
        </SectionCard>
      </main>
    </div>
  );
}
